import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CvEditorSession } from "../types/cv-editor.types";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../../components/button/types";
import Button from "../../../../../../../components/button";
import { TextInput } from "./TextInput";

// Import your shared components

interface BasicsFormData {
  name: string;
  email: string;
  phone: string;
  location: string;
}

interface BasicsValidationStepProps {
  session: CvEditorSession;
  onConfirm: (basicsData: BasicsFormData) => void;
  isLoading: boolean;
}

export const BasicsValidationStep: React.FC<BasicsValidationStepProps> = ({
  session,
  onConfirm,
  isLoading,
}) => {
  const { t } = useTranslation("cvEditor");

  // Initialize react-hook-form
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<BasicsFormData>({
    mode: "onChange", // Validate fields as the user types
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      location: "",
    },
  });

  // This effect pre-fills the form when the component mounts or session data changes.
  useEffect(() => {
    const initialBasics = session.parsedCvData?.basics || {};
    reset({
      name: initialBasics.name || "",
      email: initialBasics.email || "",
      phone: initialBasics.phone || "",
      location: initialBasics.location || "",
    });
  }, [session, reset]);

  // The handleSubmit function from react-hook-form automatically handles validation.
  // It will only call our onConfirm function if all validation rules pass.
  const onSubmit = (data: BasicsFormData) => {
    onConfirm(data);
  };

  return (
    <div className="p-6 flex flex-col h-full">
      <div className="flex-grow overflow-y-auto pr-2 -mr-2">
        <h3 className="text-lg font-medium text-gray-900 mb-1">
          {t("basicsStep.title")}
        </h3>
        <p className="text-gray-600 mb-6">{t("basicsStep.description")}</p>

        {/* The form is wrapped in a handleSubmit call */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Controller
            name="name"
            control={control}
            rules={{ required: t("basicsStep.errors.nameRequired") as string }}
            render={({ field }) => (
              <TextInput
                label={t("basicsStep.nameLabel")}
                value={field.value}
                onChange={field.onChange}
                required
                error={!!errors.name}
                helperText={errors.name?.message}
                placeholder={t("basicsStep.inputPlaceholder") || "Write here.."}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={{
              pattern: {
                value: /^\S+@\S+\.\S+$/,
                message: t("basicsStep.errors.emailInvalid") as string,
              },
            }}
            render={({ field }) => (
              <TextInput
                label={t("basicsStep.emailLabel")}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.email}
                helperText={errors.email?.message}
                required
                placeholder={t("basicsStep.inputPlaceholder") || "Write here.."}
              />
            )}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextInput
                label={t("basicsStep.phoneLabel")}
                value={field.value}
                onChange={field.onChange}
                placeholder={t("basicsStep.inputPlaceholder") || "Write here.."}
              />
            )}
          />

          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <TextInput
                label={t("basicsStep.locationLabel")}
                value={field.value}
                onChange={field.onChange}
                placeholder={t("basicsStep.inputPlaceholder") || "Write here.."}
              />
            )}
          />
        </form>
      </div>

      <div className="flex-shrink-0 mt-auto pt-4 border-t border-gray-200">
        <div className="flex justify-end">
          <Button
            type="button"
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SMALL}
            // The button calls the form's submit handler
            onClick={handleSubmit(onSubmit)}
            // Disable if the form is not valid or a mutation is in progress
            disabled={!isValid || isLoading}
            isLoading={isLoading}
            className="w-full"
          >
            {t("common.next")}
          </Button>
        </div>
      </div>
    </div>
  );
};
